import { useState, type ReactNode } from 'react';
import { cn } from '@shared/lib';

export interface StepsHintItem {
  /** Что сделать — жирным, два-три слова. */
  title: string;
  /** Короткое пояснение после тире. */
  text: string;
}

interface StepsHintProps {
  /** Ключ localStorage: закрытие запоминается в этом браузере навсегда. */
  storageKey: string;
  steps: StepsHintItem[];
  closeLabel: string;
  /** Строка-пример под шагами (расчёт стоимости) — чтобы цена бросалась в глаза. */
  example?: ReactNode;
  className?: string;
}

function readHidden(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function hideForever(key: string) {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // хранилище недоступно — плашка просто покажется снова
  }
}

/**
 * Плашка «как это работает»: нумерованные шаги в одну строку на широком экране, крестик
 * скрывает её навсегда (решение владельца 2026-10-16 — для поиска по словарю и подбора по теме).
 * Тёплый фон с оранжевыми номерами: серая плашка терялась.
 */
export function StepsHint({ storageKey, steps, closeLabel, example, className }: StepsHintProps) {
  const [hidden, setHidden] = useState(() => readHidden(storageKey));
  if (hidden) return null;

  const close = () => {
    hideForever(storageKey);
    setHidden(true);
  };

  return (
    <div className={cn('relative rounded-[18px] border border-accent/25 bg-accent/[0.06] py-3 pl-4 pr-12', className)}>
      <ol className="grid gap-2 text-[14px] leading-snug md:grid-cols-3 md:gap-4">
        {steps.map((step, i) => (
          <li key={step.title} className="flex items-center gap-2.5">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-accent font-mono text-[12px] font-semibold text-fg-inv">
              {i + 1}
            </span>
            <span className="min-w-0">
              <span className="font-semibold text-fg">{step.title}</span>
              <span className="text-muted"> — {step.text}</span>
            </span>
          </li>
        ))}
      </ol>
      {example && (
        <p className="mt-2.5 border-t border-accent/15 pt-2.5 text-[14px] leading-snug text-muted">{example}</p>
      )}
      <button
        type="button"
        onClick={close}
        aria-label={closeLabel}
        title={closeLabel}
        className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full text-muted transition-colors hover:bg-elev hover:text-fg"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}
